/**
 * @module userDropdownOptions
 * User dropdown option helpers for quotes and orders.
 *
 * **List pages** (orders, quotes): pass the raw API user list to the filter dropdown and
 * use a clearable control so operators can drop `user_id` from the request (no sentinel
 * row in the list).
 *
 * Use **withOptionalNoUser** + **findUserOption** on optional assignment fields where the
 * operator should explicitly clear an associated user (first option label: “No user”).
 * Map empty sentinel selection to `null` in submit handlers.
 *
 * Quote/order **create and upload flows** use **withOptionalNoUser** only.
 */

/** @type {{ value: string, label: string }} */
export const NO_USER_OPTION = { value: '', label: 'No user' };

/**
 * Prepends “No user” for **optional** user association on create/edit/review forms.
 * @param {Array<{ value: *, label: string }>} options - Raw API dropdown rows
 * @returns {Array<{ value: *, label: string }>}
 */
export function withOptionalNoUser(options) {
  return [NO_USER_OPTION, ...(Array.isArray(options) ? options : [])];
}

/**
 * Resolves selected row for Autocomplete (handles numeric id vs string state).
 * @param {Array<{ value: *, label: string }>|undefined|null} options - Including sentinel if prepended
 * @param {*} selectedValue - Current selection (`''`, `null`, or user id)
 * @returns {{ value: *, label: string }|null}
 */
export function findUserOption(options, selectedValue) {
  const list = Array.isArray(options) ? options : [];
  const found = list.find(
    o => String(o.value) === String(selectedValue ?? '')
  );
  return found ?? null;
}
